import React from 'react';

import SkipLink from '@/design-system/molecules/SkipLink';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useUtmParams } from '@/hooks/useUtmParams';

import { DesktopHeader } from './components/DesktopHeader';
import { MobileHeader } from './components/MobileHeader';
import { HeaderProvider } from './contexts/HeaderContext';
import { headerPropTypes } from './headerPropTypes';

/**
 *
 * The Header component is a wrapper for the MobileHeader and DesktopHeader components. It determines which header to render based on the current breakpoint and is a Global component.
 *
 * `Header` is the single UTM-capture and href-augmentation site for the
 * `patientGptCta` nav CTA (v3.1.0, AHSMKTG-4017): it threads the model-supplied
 * `patientGptCta.href` through `useUtmParams`, which is SSR-safe (bare href on
 * the server/first render, UTM-augmented post-mount). `DesktopHeader` and
 * `MobileHeader` remain pure forwarders — they receive whatever `href` this
 * component computes and forward it unmodified.
 */
const Header = ({ coveo, searchResultsPage, skipToContentLink, patientGptCta, ...props }) => {
    const breakpoint = useBreakpoint();
    const isMobile = !breakpoint?.largeplus;
    const { url: utmAugmentedHref } = useUtmParams({ url: patientGptCta?.href });
    const utmAugmentedPatientGptCta = patientGptCta
        ? { ...patientGptCta, href: utmAugmentedHref }
        : patientGptCta;
    return (
        <HeaderProvider coveo={coveo} searchResultsPage={searchResultsPage}>
            <SkipLink linkLabel={skipToContentLink?.linkLabel} />
            <MobileHeader
                {...props}
                patientGptCta={utmAugmentedPatientGptCta}
                isActive={isMobile}
            />
            <DesktopHeader
                {...props}
                patientGptCta={utmAugmentedPatientGptCta}
                isActive={!isMobile}
            />
            {props.alertBannerComponent || null}
        </HeaderProvider>
    );
};

Header.propTypes = {
    ...headerPropTypes,
};

export default Header;
